import { useMemo, useEffect, useState } from 'react'

import {
  Group,
  Radio,
  Tooltip,
  Alert,
  Checkbox,
  Box,
  Loader,
  Text,
  Select,
} from '@mantine/core'
import type { GetDriverIQUdfFields } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_DRIVER_IQ_UDF_FIELDS } from 'src/graphql/driverIQUdfFields.graphql'
import {
  BULK_CREATE_PACKAGE_ORDERS,
  GET_APPLICANTS_FOR_BACKGROUND_CHECK,
} from 'src/graphql/packageOrders.graphql'
import { REQUEST_PACKAGES } from 'src/graphql/packages.graphql'
import { PackageType } from 'src/graphql/types/package'
import { StepType } from 'src/graphql/types/steps'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconDocumentSearch from 'src/icons/IconDocumentSearch'

const MAX_BULK_BACKGROUND_CHECK_APPLICANTS = 50

type UdfField = GetDriverIQUdfFields['driverIQUdfFields'][number]

// Type definitions for bulk background check results
type BulkResult = {
  applicantId: string
  success: boolean
  errorMsg?: string
  successMsg?: string
}

// Helper to check if an applicant is eligible for background check
const checkApplicantEligibility = (applicantData) => {
  const issues = []

  if (!applicantData?.application) {
    issues.push('No application')
    return issues
  }

  const app = applicantData.application

  if (!app.ssn) issues.push('Missing SSN')
  if (!app.dob) issues.push('Missing DOB')
  if (!app.license?.licenseNumber || !app.license?.state) {
    issues.push('Missing license info')
  }
  if (
    (!app.addresses || app.addresses.length === 0) &&
    !applicantData.address
  ) {
    issues.push('Missing address')
  }
  if (!app.consents || app.consents.length === 0) {
    issues.push('Missing consents')
  } else if (app.consents.some((c) => !c.signed)) {
    issues.push('Unsigned consents')
  }

  return issues
}

const BulkBackgroundCheckDrawer = ({
  applicants,
  refetchQueries = [],
  onClose,
  setLoading,
  setResults,
}) => {
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  )
  const [fcraConsent, setFcraConsent] = useState<boolean>(false)
  const [currentLicenseOnly, setCurrentLicenseOnly] = useState<boolean>(false)
  /** DriverIQ UDF slot (UDF1|UDF2|UDF3) -> selected option id */
  const [udfSelections, setUdfSelections] = useState<Record<string, string>>({})

  // Get applicant IDs for the eligibility query
  const applicantIds = useMemo(
    () => applicants.map((a) => a.applicantId),
    [applicants]
  )

  // QUERIES:
  const { data: packagesData } = useQuery(REQUEST_PACKAGES)
  const { data: udfData } = useQuery<GetDriverIQUdfFields>(
    GET_DRIVER_IQ_UDF_FIELDS
  )

  // Fetch eligibility data for selected applicants
  const {
    data: eligibilityData,
    loading: eligibilityLoading,
    error: eligibilityError,
  } = useQuery(GET_APPLICANTS_FOR_BACKGROUND_CHECK, {
    variables: {
      filters: {
        applicantIds: applicantIds,
      },
      pageInfo: { limit: MAX_BULK_BACKGROUND_CHECK_APPLICANTS, offset: 0 },
    },
    skip: applicantIds.length === 0,
  })

  // Check eligibility for each applicant
  const ineligibleApplicants = useMemo(() => {
    if (!eligibilityData?.applicants?.items) return []

    const eligibilityMap = new Map(
      eligibilityData.applicants.items.map((a) => [a.applicantId, a])
    )

    return applicants
      .map((applicant) => {
        const data = eligibilityMap.get(applicant.applicantId)
        const issues = checkApplicantEligibility(data)
        return issues.length > 0
          ? { ...applicant, issues: issues.join(', ') }
          : null
      })
      .filter(Boolean)
  }, [applicants, eligibilityData])

  const eligibilityUnavailable =
    applicants.length > 0 &&
    !eligibilityLoading &&
    !eligibilityData?.applicants?.items

  const packages = packagesData?.packages || []

  // Check if selected package is HireRight (requires FCRA consent)
  const selectedPackage = useMemo(
    () => packages.find((pkg) => pkg.packageId === selectedPackageId),
    [packages, selectedPackageId]
  )

  const isHireRightPackage = selectedPackage?.packageType === 'HIRE_RIGHT'
  const isDriverIQPackage =
    selectedPackage?.packageType === PackageType.DRIVER_IQ
  // Only MVR-bearing packages are affected by the license scope toggle
  const showCurrentLicenseOnly = !!selectedPackage?.stepType?.includes(
    StepType.MVR
  )

  const udfFields: UdfField[] = useMemo(
    () =>
      udfData?.driverIQUdfFields
        ?.slice()
        .sort((a, b) => a.sortOrder - b.sortOrder) || [],
    [udfData]
  )
  // The dropdowns only mean anything for DriverIQ, and the submitted value is
  // guarded the same way so picks made while a DriverIQ package was selected
  // can't ride along on an order for another provider.
  const showUdfFields = isDriverIQPackage && udfFields.length > 0
  const missingRequiredUdf =
    isDriverIQPackage &&
    udfFields.some((f) => f.isRequired && !udfSelections[f.slot])
  const udfSelectionsInput = isDriverIQPackage
    ? Object.entries(udfSelections)
        .filter(([, optionId]) => !!optionId)
        .map(([slot, optionId]) => ({
          slot,
          driverIQUdfFieldOptionId: optionId,
        }))
    : []

  // MUTATIONS:
  const [bulkCreateOrders, { loading: bulkLoading }] = useMutation(
    BULK_CREATE_PACKAGE_ORDERS,
    {
      refetchQueries: refetchQueries,
      onCompleted: (data) => {
        const results = data?.bulkCreatePackageOrders?.applicants || []
        const successCount = results.filter((r) => r.success).length
        const failCount = results.filter((r) => !r.success).length

        if (failCount > 0) {
          console.error(
            'Failed background checks:',
            results.filter((r) => !r.success)
          )
        }

        if (setResults) {
          setResults(
            results.map((r) => ({
              applicantId: r.applicantId,
              success: r.success,
              errorMsg: r.errorMsg || 'Failed to order background check',
            }))
          )
        }

        if (failCount === 0) {
          toast('Background checks ordered successfully', 'success')
        } else if (successCount === 0) {
          toast('Failed to order background checks', 'error')
        } else {
          toast(
            `Ordered ${successCount} background checks, ${failCount} failed`,
            'warning'
          )
        }
      },
      onError: (error) => {
        console.error('Bulk background check error:', error)
        if (setResults) {
          setResults(
            applicants.map((a) => ({
              applicantId: a.applicantId,
              success: false,
              errorMsg: 'Failed to order background check',
            }))
          )
        }
        toast('Unable to order background checks, please try again.', 'error')
      },
    }
  )

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkLoading)
    }
  }, [bulkLoading, setLoading])

  // Reset per-package choices when package changes
  useEffect(() => {
    setFcraConsent(false)
    setCurrentLicenseOnly(false)
    setUdfSelections({})
  }, [selectedPackageId])

  const handleSubmit = () => {
    bulkCreateOrders({
      variables: {
        input: {
          applicantIds: applicants.map((applicant) => applicant.applicantId),
          packageId: selectedPackageId,
          fcraLegalRequirementsConsent: fcraConsent,
          currentLicenseOnly: showCurrentLicenseOnly && currentLicenseOnly,
          driverIQUdfSelections: udfSelectionsInput,
        },
      },
    })
  }

  const canSubmit =
    selectedPackageId &&
    applicants.length > 0 &&
    applicants.length <= MAX_BULK_BACKGROUND_CHECK_APPLICANTS &&
    (!isHireRightPackage || fcraConsent) &&
    !missingRequiredUdf &&
    !eligibilityLoading &&
    !eligibilityError &&
    !eligibilityUnavailable &&
    ineligibleApplicants.length === 0

  const getTooltipLabel = () => {
    if (applicants.length > MAX_BULK_BACKGROUND_CHECK_APPLICANTS) {
      return `Maximum ${MAX_BULK_BACKGROUND_CHECK_APPLICANTS} applicants allowed`
    }
    if (eligibilityLoading) {
      return 'Checking applicant eligibility...'
    }
    if (eligibilityError) {
      return 'Unable to verify eligibility. Please try again.'
    }
    if (eligibilityUnavailable) {
      return 'Eligibility results unavailable. Please refresh and try again.'
    }
    if (ineligibleApplicants.length > 0) {
      return `${ineligibleApplicants.length} applicant(s) are missing required data`
    }
    if (!selectedPackageId) {
      return 'Please select a background check package'
    }
    if (isHireRightPackage && !fcraConsent) {
      return 'Please acknowledge FCRA legal requirements'
    }
    if (missingRequiredUdf) {
      const missing = udfFields
        .filter((f) => f.isRequired && !udfSelections[f.slot])
        .map((f) => f.label)
        .join(', ')
      return `Please select ${missing}`
    }
    return ''
  }

  const packageOptions = packages.map((pkg) => ({
    value: pkg.packageId,
    label: pkg.name,
    description: pkg.description,
    packageType: pkg.packageType,
  }))

  return (
    <>
      <div className="flex flex-col gap-4">
        {applicants.length > MAX_BULK_BACKGROUND_CHECK_APPLICANTS && (
          <div className="border-doubleNickel-red-200 bg-doubleNickel-red-50 flex flex-row items-center gap-2 rounded-lg border p-3">
            <IconAlertTriangle className="text-doubleNickel-red-500 h-4 w-4" />
            <Text size="xs" className="text-doubleNickel-red-700">
              Maximum {MAX_BULK_BACKGROUND_CHECK_APPLICANTS} applicants per
              batch. Please reduce your selection.
            </Text>
          </div>
        )}
        {eligibilityLoading ? (
          <div className="flex items-center gap-2 text-sm text-doubleNickel-gray-500">
            <Loader size="xs" /> Checking applicant eligibility...
          </div>
        ) : eligibilityError || eligibilityUnavailable ? (
          <div className="border-doubleNickel-red-200 bg-doubleNickel-red-50 flex flex-row items-center gap-2 rounded-lg border p-3">
            <IconAlertTriangle className="text-doubleNickel-red-500 h-4 w-4" />
            <Text size="xs" className="text-doubleNickel-red-700">
              Unable to verify applicant eligibility. Please refresh and try
              again before submitting.
            </Text>
          </div>
        ) : ineligibleApplicants.length > 0 ? (
          <div className="border-doubleNickel-red-200 bg-doubleNickel-red-50 flex flex-row gap-2 rounded-lg border p-3">
            <IconAlertTriangle className="text-doubleNickel-red-500 mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex flex-col gap-1">
              <Text size="xs" fw={600} className="text-doubleNickel-red-700">
                {ineligibleApplicants.length} applicant
                {ineligibleApplicants.length > 1 ? 's' : ''} may fail due to
                missing data:
              </Text>
              <ul className="text-doubleNickel-red-700 ml-4 max-h-[100px] list-disc overflow-y-auto text-xs">
                {ineligibleApplicants.map((a) => (
                  <li key={a.applicantId}>
                    <strong>{a.name}</strong>: {a.issues}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          applicants.length > 0 && (
            <div className="flex flex-row items-center gap-2 rounded-lg border border-doubleNickel-green-200 bg-doubleNickel-green-50 p-3">
              <IconAlertTriangle className="text-doubleNickel-green-500 h-4 w-4" />
              <Text size="xs" className="text-doubleNickel-green-700">
                All selected applicants appear eligible for background checks.
              </Text>
            </div>
          )
        )}

        {packages.length > 0 ? (
          <>
            <div className="flex flex-row items-center justify-between">
              <Text size="xs">Package</Text>
              <div className="w-[250px]">
                <Select
                  placeholder="Select a package"
                  data={packageOptions}
                  value={selectedPackageId}
                  onChange={setSelectedPackageId}
                  searchable
                  clearable
                  nothingFoundMessage="No packages found"
                  checkIconPosition="right"
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right text-doubleNickel-gray-900',
                    dropdown: 'border-doubleNickel-gray-200',
                    option:
                      'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                  }}
                />
              </div>
            </div>

            {selectedPackage && (
              <div className="flex flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
                <div className="flex flex-col gap-1">
                  <div className="text-sm font-semibold text-doubleNickel-gray-700">
                    {selectedPackage.name}
                  </div>
                  {selectedPackage.description && (
                    <div className="text-xs text-doubleNickel-gray-500">
                      {selectedPackage.description}
                    </div>
                  )}
                  <div className="mt-1 text-xs text-doubleNickel-gray-400">
                    Provider: {selectedPackage.packageType}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4">
            <Empty
              title="No background check packages found"
              subtitle="Please contact Double Nickel to set up background check packages for your account."
            />
          </div>
        )}

        {showUdfFields &&
          udfFields.map((field) => (
            <div
              key={field.driverIQUdfFieldId}
              className="flex flex-row items-center justify-between"
            >
              <Text size="xs">
                {field.label}
                {field.isRequired && ' *'}
              </Text>
              <div className="w-[250px]">
                <Select
                  data-testid={`udf-select-${field.slot}`}
                  placeholder={`Select ${field.label.toLowerCase()}`}
                  data={field.options.map((o) => ({
                    value: o.driverIQUdfFieldOptionId,
                    label: o.label,
                  }))}
                  value={udfSelections[field.slot] || null}
                  onChange={(value) =>
                    setUdfSelections((current) => ({
                      ...current,
                      // Clearing yields null; keep the map string-valued so the
                      // required check and the submitted list both stay simple.
                      [field.slot]: value ?? '',
                    }))
                  }
                  searchable
                  clearable={!field.isRequired}
                  nothingFoundMessage="No options found"
                  checkIconPosition="right"
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right text-doubleNickel-gray-900',
                    dropdown: 'border-doubleNickel-gray-200',
                    option:
                      'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                  }}
                />
              </div>
            </div>
          ))}

        {showCurrentLicenseOnly && (
          <Checkbox
            checked={currentLicenseOnly}
            onChange={(e) => setCurrentLicenseOnly(e.currentTarget.checked)}
            data-testid="current-license-only"
            label={
              <span className="text-sm text-doubleNickel-gray-700">
                Run checks on the current license only
              </span>
            }
            description="Prior licenses on each application will not be sent to the provider."
          />
        )}

        {isHireRightPackage && (
          <div className="rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-50 p-4">
            <Checkbox
              checked={fcraConsent}
              onChange={(e) => setFcraConsent(e.currentTarget.checked)}
              label={
                <span className="text-sm text-doubleNickel-gray-700">
                  I acknowledge that I have reviewed the{' '}
                  <strong>FCRA legal requirements</strong> and confirm that
                  proper disclosures and authorizations have been obtained from
                  each applicant.
                </span>
              }
            />
          </div>
        )}
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkLoading}
        />
        <Tooltip disabled={canSubmit} label={getTooltipLabel()}>
          <Button
            text={'Run Background Check'}
            variant="filled"
            lefticon={<IconDocumentSearch />}
            disabled={!canSubmit}
            loading={bulkLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkBackgroundCheckDrawer
